/**
 * Session Slice — Session lifecycle state + actions
 *
 * Handles: startNewSession, loadSession, triggerOpening, leaveCurrentSession
 */

import type { StateCreator } from "zustand";
import { uuidv7 } from "uuidv7";
import { api } from "../../services/api";
import { getSocket } from "../../services/socket";
import { emitEvent } from "../../services/socketValidation";
import { useAuthStore } from "../authStore";
import { resultToErrorInfo } from "../../utils/conversationErrors";
import { useStreamingTTSStore } from "../streamingTTSStore";
import { getAudioMode } from "../../hooks/useAudioMode";
import i18n from "../../i18n";
import type { ConversationState } from "../conversationTypes";
import type { AnalyzedMessage, MessageStatus } from "../../types/conversation";
import {
  log,
  serverMessageToLocal,
  setupStreamingCore,
  repairMessageText,
  joinSessionRoom,
  leaveSessionRoom,
  SCHEMA_VERSION,
  AI_OPENING_TIMEOUT_MS,
  getStreamStallTimeoutMs,
} from "../conversationHelpers";
import type { StartSessionAck } from "../../generated/api/socket-events";

export interface SessionSlice {
  currentSessionId: string | null;
  flowType: string;
  hasAnalysis: boolean;
  hasOpening: boolean;

  startNewSession: (
    flowType?: string,
    options?: { focusPhoneme?: string; focusGrammarPoint?: string },
  ) => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  triggerOpening: () => Promise<void>;
  leaveCurrentSession: () => void;
}

export const createSessionSlice: StateCreator<
  ConversationState,
  [],
  [],
  SessionSlice
> = (set, get) => ({
  currentSessionId: null,
  flowType: 'conversation',
  hasAnalysis: true,
  hasOpening: false,

  /**
   * Start a new conversation session (creates on server).
   *
   * `options.focusPhoneme` is the Sound Map's "practice this sound"
   * pre-targeting (docs/specs/mobile/sound-map.mobile.md);
   * `options.focusGrammarPoint` is its grammar counterpart, set when the
   * learner picks a competence off the Practice tab's recommendations
   * (competence-mastery.api.md § CM-020). Either is omitted entirely from
   * the request when unset.
   *
   * Passing BOTH is allowed by the contract but pointless: the server emits at
   * most one Learner-Chosen Focus block and the phoneme wins, so the grammar
   * point would be stored and never rendered. Callers pick one.
   */
  startNewSession: async (
    flowType?: string,
    options?: { focusPhoneme?: string; focusGrammarPoint?: string },
  ) => {
    const previousSessionId = get().currentSessionId;
    if (previousSessionId) leaveSessionRoom(previousSessionId);

    // Get active course preference for session creation
    // Use require() to avoid dynamic import() which isn't supported in Jest CJS mode
    const { getActiveCoursePreference } = require('../userPreferencesStore') as typeof import('../userPreferencesStore');
    const pref = getActiveCoursePreference();
    const courseId = pref?.courseId ?? 'es-en';
    const sourceLocale = pref?.sourceLocale ?? 'es-419';
    const targetLocale = pref?.targetLocale ?? 'en-US';

    const result = await api.createSession({
      flowType: flowType ?? 'conversation',
      courseId,
      sourceLocale,
      targetLocale,
      ...(options?.focusPhoneme ? { focusPhoneme: options.focusPhoneme } : {}),
      ...(options?.focusGrammarPoint
        ? { focusGrammarPoint: options.focusGrammarPoint }
        : {}),
    });

    if (!result.ok) {
      const errorInfo = resultToErrorInfo(result);
      set({
        currentSessionId: null,
        messages: [],
        streamingText: '',
        streamingMessageId: null,
        error: errorInfo.message,
        errorInfo,
      });
      return;
    }

    const session = result.data;
    set({
      currentSessionId: session.id,
      flowType: session.flowType,
      hasAnalysis: session.hasAnalysis,
      hasOpening: session.hasOpening,
      messages: [],
      streamingText: '',
      streamingMessageId: null,
      error: null,
      errorInfo: null,
    });
    joinSessionRoom(session.id);
    log.info("Session created", { sessionId: session.id });
  },

  /**
   * Load existing session from server
   */
  loadSession: async (sessionId: string) => {
    const previousSessionId = get().currentSessionId;
    if (previousSessionId && previousSessionId !== sessionId) {
      leaveSessionRoom(previousSessionId);
    }

    log.debug("Loading session", { sessionId });

    const { user } = useAuthStore.getState();
    const result = await api.getSessionMessages(sessionId);

    if (!result.ok) {
      // Clear messages so ChatScreen's error banner (gated on
      // `messages.length === 0`) can surface. Without this, opening
      // a failed-load session right after a successful one would
      // leave the previous session's messages on screen with no
      // error indication — bad UX in production, and the cause of
      // CONV-003's branch failures where `chat-error-banner` never
      // rendered after an injected GET messages error.
      const errorInfo = resultToErrorInfo(result);
      set({
        currentSessionId: sessionId,
        messages: [],
        streamingText: '',
        streamingMessageId: null,
        error: errorInfo.message,
        errorInfo,
      });
      return;
    }

    const data = result.data;
    const messages = data.messages.map((m) =>
      serverMessageToLocal(m, sessionId, user?.id || "")
    );

    set({
      currentSessionId: sessionId,
      flowType: data.session.flowType,
      hasAnalysis: data.session.hasAnalysis,
      hasOpening: data.session.hasOpening ?? false,
      messages,
      streamingText: '',
      streamingMessageId: null,
      error: null,
      errorInfo: null,
    });
    joinSessionRoom(sessionId);
    log.info("Session loaded from server", {
      messageCount: messages.length,
    });

    // Auto-trigger AI opening if session has no messages yet
    if (data.session.hasOpening && messages.length === 0) {
      get().triggerOpening();
    }
  },

  /**
   * Trigger AI-initiated opening for the current session.
   * Emits `start_session` and pipes the AI's opening response through
   * the same streaming infrastructure as regular messages.
   */
  triggerOpening: async () => {
    const { currentSessionId: sessionId, isStreaming } = get();
    if (!sessionId || isStreaming) return;

    const socket = getSocket();
    if (!socket) {
      log.warn("Socket not available for opening");
      return;
    }

    const { user } = useAuthStore.getState();

    // Create an optimistic AI placeholder message
    const aiId = uuidv7();
    const aiMessage: AnalyzedMessage = {
      id: aiId,
      role: "assistant",
      type: "text",
      text: "",
      userId: user?.id || "",
      status: "streaming",
      sessionId,
      createdAt: new Date(),
    };

    set({
      isStreaming: true,
      streamingText: '',
      streamingMessageId: aiId,
      messages: [aiMessage],
    });

    // Start TTS streaming if audio mode is auto_play
    if (getAudioMode() === 'auto_play') {
      useStreamingTTSStore.getState().startStreaming(aiId, get().currentSessionId ?? undefined);
    }

    await new Promise<void>((resolve, reject) => {
      // eslint-disable-next-line prefer-const -- declared here, assigned conditionally below
      let conversationTimeout: ReturnType<typeof setTimeout> | undefined;

      // Both the long upper-bound timeout and the short "no proof of
      // life" stall guard converge on the same UX: replace the spinner
      // with a retryable error bubble. The `code` differs so the user
      // can tell them apart in logs / Sentry.
      const surfaceTimeout = (code: 'AI_OPENING_TIMEOUT' | 'STREAM_STALLED', logMessage: string) => {
        clearTimeout(conversationTimeout);
        streamHandle.cleanup();
        log.error(logMessage, new Error(code), {
          sessionId,
          // Sentry tags — see comment in conversationHelpers'
          // surfaceTimeout. `streamPath: 'opening'` discriminates the
          // AI-initiated path from user messages on the dashboard.
          _tags: { timeoutCode: code, streamPath: 'opening' },
        });
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === aiId
              ? {
                  ...m,
                  text: s.streamingText || '',
                  status: 'error' as MessageStatus,
                  errorInfo: {
                    type: 'network' as const,
                    code,
                    message: i18n.t('errors.api.TIMEOUT', { defaultValue: 'Request timed out' }),
                    retryable: true,
                  },
                }
              : m
          ),
          isStreaming: false,
          streamingText: '',
          streamingMessageId: null,
        }));
        reject(new Error(code));
      };

      const streamHandle = setupStreamingCore({
        socket,
        aiId,
        set,
        onMessageSaved: () => {
          // No user message to track for openings
        },
        onStreamError: (_errorInfo, rawData) => {
          clearTimeout(conversationTimeout);
          log.error("Opening stream error", new Error(rawData.message || rawData.code || 'Unknown'), rawData);
          reject(new Error(rawData.message));
        },
        onComplete: () => {
          clearTimeout(conversationTimeout);
          resolve();
        },
        onStreamTextMissing: (aiMessageId) => {
          repairMessageText(sessionId, aiMessageId, set);
        },
        // Short "no proof of life" guard — bounds the spinner-with-no-
        // feedback window at ~30s instead of letting the user stare at
        // it for 155s. The opening has no audio analysis, so the first
        // inbound event (token or message_saved) should arrive within
        // seconds on a healthy backend. If nothing at all comes through,
        // the stream is treated as stalled and surfaced as a retryable
        // error — same shape as the long-tail timeout, different code.
        // Read via getter so dev.override-stall-timeout (used by the
        // STALL-001/002/003 E2E stories) can lower it to ~3s for tests.
        stallTimeoutMs: getStreamStallTimeoutMs(),
        onStreamStalled: () => {
          surfaceTimeout(
            'STREAM_STALLED',
            'Opening stalled — no events within stall window',
          );
        },
      });

      // Emit start_session
      emitEvent(socket, "start_session", {
        v: SCHEMA_VERSION,
        sessionId,
      }, (ackResponse: StartSessionAck) => {
        if (ackResponse && !ackResponse.ok) {
          log.error("Server rejected start_session", {
            code: ackResponse.code,
            message: ackResponse.message,
            category: ackResponse.category,
          });
          streamHandle.cleanup();
          set({
            isStreaming: false,
            streamingText: '',
            streamingMessageId: null,
            messages: get().messages.filter((m) => m.id !== aiId),
          });
          reject(new Error(ackResponse.message || "Opening rejected"));
        }
      });

      // Long upper-bound safety net — the stall guard above should
      // usually fire first; this only matters when events ARE arriving
      // (so the stall guard keeps resetting) but the stream never
      // completes within the worst-case pipeline budget.
      //
      // AI_OPENING_TIMEOUT_MS (85s) — not CONVERSATION_TIMEOUT_MS (145s) —
      // because the AI-initiated opening path carries no audio, so the
      // 60s pronunciation-analysis slice is dead weight. User-initiated
      // first messages take the regular send_message path and use
      // CONVERSATION_TIMEOUT_MS like any other user message.
      conversationTimeout = setTimeout(() => {
        surfaceTimeout('AI_OPENING_TIMEOUT', 'AI opening timed out');
      }, AI_OPENING_TIMEOUT_MS);
    }).catch((err) => {
      log.error("Opening failed", err);
    });
  },

  /**
   * Leave the current session's socket room and clear session state.
   * Called when navigating away from the chat screen.
   */
  leaveCurrentSession: () => {
    const sessionId = get().currentSessionId;
    if (sessionId) leaveSessionRoom(sessionId);
    set({ currentSessionId: null });
  },
});
